import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  DateTypeFilter,
  FeatureName,
  getLocalTime,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import { Colors } from 'libs/shared-ui/src/themes';
import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import {
  KeyboardTypeOptions,
  Platform,
  Pressable,
  StyleProp,
  StyleSheet,
  Text,
  TextProps,
  View,
  ViewStyle,
} from 'react-native';
import { useOpenedState } from '../../../hooks';
import { Icon } from '../../crop-circle/icons';
import { tokens } from '../../crop-circle/tokens';
import { StyledTextInput } from '../controlledTextInput';
import { DatePickerSelectorOld } from './datePickerSelector-old';
import { datePickerStyle as styles } from './datePickerStyle';
import { DatePickerWheel } from './datePickerWheel';
import { DateRangePickerSelector } from './dateRangePickerSelector';
import { DateRangeSelectorProps } from './dateRangeSelector';
import { WebDateTimePicker } from './webDateTimePicker';

const cropCircleStyles = StyleSheet.create({
  container: {
    borderRadius: 4,
    backgroundColor: tokens.colors.neutral[200],
    borderWidth: 0,
    minHeight: 48,
    height: 48,
    flex: 1, // Share space equally when in a row (e.g. blackout dates)
    padding: 0, // Override dateRangeFieldContainer's padding: 8 - text and icon have their own 16px
    paddingHorizontal: 0,
    paddingVertical: 0,
    justifyContent: 'center',
    flexDirection: 'column',
  },
  inputContainer: {
    paddingHorizontal: 0,
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between', // Keep text left, calendar icon right
  },
  inputText: {
    ...tokens.typography.body.small,
    color: tokens.colors.brand.black,
    paddingLeft: 16, // Exactly 16px left padding for text (design)
    paddingRight: 0,
    marginRight: 0,
    flex: 1,
    borderRadius: 0,
    backgroundColor: 'transparent',
    ...Platform.select({
      ios: {
        lineHeight: undefined,
      },
    }),
  },
  calendarIcon: {
    margin: 0,
    marginLeft: 0, // Override any marginLeft from old styles
    marginRight: 0,
    padding: 0,
    paddingRight: 16, // Exactly 16px right padding for icon (design)
    alignItems: 'center',
    justifyContent: 'center',
  },
});

type DatePickerSelectorProps = DateRangeSelectorProps & {
  name: string;
  placeholder?: string;
  textArea?: boolean;
  innerRef?: any;
  keyboardType?: KeyboardTypeOptions;
  height?: number;
  label?: string;
  isStartDate?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  mode?: 'date' | 'time' | 'datetime';
  maxYears?: number;
  calendarIconStyle?: StyleProp<ViewStyle>;
  /** Optional style for the native date picker modal container (e.g. background). Applied on iOS. */
  pickerContainerStyleIOS?: StyleProp<ViewStyle>;
  /** Optional style for the native date picker modal content. Applied on iOS. */
  modalStyleIOS?: StyleProp<ViewStyle>;
  /** Optional style for the label above the date field. */
  labelStyle?: TextProps['style'];
  /** Optional color for the calendar/clock icon. */
  calendarIconColor?: string;
  /** Optional style for the iOS modal confirm button container. */
  confirmButtonStyleIOS?: StyleProp<ViewStyle>;
  /** When true, disables the input and prevents the calendar dropdown from opening. */
  disabled?: boolean;
  /** When true, hides the calendar icon and adjusts clear button position. */
  hideCalendarIcon?: boolean;
};

// TODO: Crop Circle Redesign: Remove after feature is fully rolled out
export const DatePickerSelector = (props: DatePickerSelectorProps) => {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  if (!isCropCircleRedesignEnabled) {
    return <DatePickerSelectorOld {...props} disabled={props.disabled} />;
  }
  return <DatePickerSelectorBase {...props} />;
};

const DatePickerSelectorBase = (props: DatePickerSelectorProps) => {
  const isWeb = Platform.OS === 'web';
  const datePickerStartDateRef = useRef<View>(null!);
  const {
    onOpen: onDateRangePickerSelectorOpen,
    onClose: onDateRangePickerSelectorClose,
    isOpened: isDateRangePickerSelectorOpened,
  } = useOpenedState();

  const handleDateRangeSelector = () => {
    if (props.disabled) return;
    props.setValue('dateType', DateTypeFilter.CUSTOMDATE);
    onDateRangePickerSelectorOpen();
  };

  if (props.mode === 'time' && isWeb) {
    return (
      <WebDateTimePicker
        control={props.control}
        setValue={props.setValue}
        isStartDate={props.isStartDate}
        name={props.name}
        label={props.label}
      />
    );
  }


  return (
    <View
      style={[
        styles.dateRangeFieldContainer,
        isDateRangePickerSelectorOpened &&
          styles.dateRangeFieldContainerActiveRedesign,
        cropCircleStyles.container,
        isDateRangePickerSelectorOpened && styles.dateRangeFieldContainerActive,
        props.containerStyle,
        isWeb && { width: 'auto', minWidth: 0 },
      ]}
    >
      <Controller
        control={props.control}
        name={props.name}
        render={({ field: { onChange, onBlur, value } }) => {
          const valueFormat =
            props.mode === 'time' && value ? getLocalTime(value) : value;
          const hasValue = !!valueFormat;
          const isOpen = isDateRangePickerSelectorOpened;

          const handleClear = () => {
            onChange('');
          };

          return (
            <>
              {props.label && (
                <View style={[styles.labelContainer]}>
                  <Text
                    style={[styles.label, props.labelStyle]}
                    numberOfLines={1}
                  >
                    {props.label}
                  </Text>
                </View>
              )}
              <Pressable
                onPress={() => {
                  if (!isOpen && !props.disabled) {
                    handleDateRangeSelector();
                  }
                }}
                style={[styles.inputTouchArea, props.disabled && { opacity: 0.4 }]}
                disabled={props.disabled}
              >
                <View style={cropCircleStyles.inputContainer}>
                  <StyledTextInput
                    onChangeText={onChange}
                    onBlur={onBlur}
                    value={valueFormat ?? ''}
                    placeholder={props.placeholder}
                    editable={false}
                    textArea={props.textArea}
                    innerRef={props.innerRef}
                    keyboardType={props.keyboardType}
                    height={props.height ?? 48}
                    backgroundColor={'transparent'}
                    inputInnerStyle={cropCircleStyles.inputText}
                    pointerEvents="none"
                    disableInnerStyle
                  />
                  {!props.hideCalendarIcon && (
                    <View style={[cropCircleStyles.calendarIcon, props.calendarIconStyle]}>
                      <Icon
                        name={props.mode === 'time' ? 'Clock A' : 'Calendar'}
                        size={18}
                        color={props.calendarIconColor ?? tokens.colors.brand.black}
                      />
                    </View>
                  )}
                </View>
              </Pressable>
              {(isOpen || hasValue || !props.disabled) && (
                <View style={[styles.datePickerIcons, props.hideCalendarIcon && { right: 16 }]}>
                  {isOpen && (
                    <MaterialCommunityIcons
                      name="chevron-up"
                      size={20}
                      color={Colors.grayText}
                    />
                  )}
                  {hasValue && !props.disabled && (
                    <Pressable onPress={handleClear} hitSlop={8}>
                      <MaterialCommunityIcons
                        name="close"
                        size={16}
                        color={Colors.grayText}
                      />
                    </Pressable>
                  )}
                </View>
              )}
              <View ref={datePickerStartDateRef}>
                {isWeb ? (
                  <DateRangePickerSelector
                    control={props.control}
                    setValue={props.setValue}
                    onDateSelected={onChange}
                    anchorRef={datePickerStartDateRef}
                    isVisible={isDateRangePickerSelectorOpened}
                    onClose={onDateRangePickerSelectorClose}
                    value={value}
                    isStartDate={props.isStartDate}
                    mode={props.mode}
                    maxYears={props.maxYears}
                    minDate={props.minDate}
                  />
                ) : (
                  <DatePickerWheel
                    control={props.control}
                    setValue={props.setValue}
                    onDateSelected={onChange}
                    isVisible={isDateRangePickerSelectorOpened}
                    onClose={onDateRangePickerSelectorClose}
                    isStartDate={props.isStartDate}
                    mode={props.mode}
                    name={props.name}
                    minDate={props.minDate}
                    pickerContainerStyleIOS={props.pickerContainerStyleIOS as any}
                    modalStyleIOS={props.modalStyleIOS as any}
                    confirmButtonStyleIOS={props.confirmButtonStyleIOS as any}
                  />
                )}
              </View>
            </>
          );
        }}
      />
    </View>
  );
};
